import { z } from "zod";
import { optional, storableText } from "./appointments";
import { recordId } from "./contracts";
import { absentOrBlankAsNull, searchTextUpTo, unset } from "./fields";
import { SETTINGS_SHORT_TEXT_MAX_LENGTH } from "./organization-settings";

export const ADMIN_PEOPLE_PAGE_SIZE_MAX = 100;

export const ADMIN_PASSWORD_MIN_LENGTH = 6;

export const ADMIN_PASSWORD_MAX_LENGTH = 72;

export const PHONE_DIGITS_MIN = 7;

const SEARCH_MAX_LENGTH = 200;

export const DIRECTORY_SHAPE_LABELS = {
  admin_app: "Admin app",
  pmc_user: "PMC user",
  vendor_user: "Vendor user",
  subs_user: "Sub-contractor user",
  organization_user: "Organization user",
  vendor_individual: "Vendor-linked individual",
  subs_individual: "Sub-linked individual",
  linked_individual: "Linked individual",
  property_user: "Property user",
  auth_only: "Auth only",
  unlinked_app_user: "Unlinked app user",
} as const;

export type DirectoryShapeKey = keyof typeof DIRECTORY_SHAPE_LABELS;

export const directoryShapeKey = z.enum(Object.keys(DIRECTORY_SHAPE_LABELS) as [DirectoryShapeKey, ...DirectoryShapeKey[]]);

export const directoryOrganizationType = z.enum(["pmc", "vendor", "subs"]);
export type DirectoryOrganizationType = z.infer<typeof directoryOrganizationType>;

export const adminPeopleQuery = z.object({
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(ADMIN_PEOPLE_PAGE_SIZE_MAX).default(20)),
  search: optional(searchTextUpTo(SEARCH_MAX_LENGTH)),
  shape: optional(directoryShapeKey),
  orgType: optional(directoryOrganizationType),
});
export type AdminPeopleQuery = z.infer<typeof adminPeopleQuery>;

const memberStatus = z.enum(["active", "invited", "pending", "suspended"]);

export const directoryOrganization = z.object({ id: recordId, name: z.string(), type: z.string(), email: z.string(), isInactive: z.boolean() });
export type DirectoryOrganization = z.infer<typeof directoryOrganization>;

export const directoryShape = z.object({ key: directoryShapeKey, label: z.string() });
export type DirectoryShape = z.infer<typeof directoryShape>;

export const directoryMembership = z.object({
  id: recordId,
  status: memberStatus,
  organization: directoryOrganization,
  role: z.object({ name: z.string() }),
});
export type DirectoryMembership = z.infer<typeof directoryMembership>;

export const directoryWorkerContract = z.object({
  id: recordId,
  status: memberStatus,
  organization: directoryOrganization,
  services: z.array(z.string()),
});
export type DirectoryWorkerContract = z.infer<typeof directoryWorkerContract>;

export const directoryPropertyAssignment = z.object({
  id: recordId,
  property: z.object({ id: recordId, name: z.string(), address: z.string(), organization: directoryOrganization.nullable() }),
  role: z.string().optional(),
  notes: z.string().optional(),
  isPrimaryContact: z.boolean(),
});
export type DirectoryPropertyAssignment = z.infer<typeof directoryPropertyAssignment>;

export const adminDirectoryPerson = z.object({
  id: recordId,
  userId: recordId.nullable(),
  authId: recordId,
  name: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  image: z.string().optional(),
  authEmail: z.string().nullable(),
  authRole: z.string().nullable(),
  authConfirmedAt: z.string().nullable(),
  authLastSignInAt: z.string().nullable(),
  authBannedUntil: z.string().nullable(),
  createdAt: z.string().nullable(),
  shapes: z.array(directoryShape),
  organizationUsers: z.array(directoryMembership),
  individualLinks: z.array(directoryWorkerContract),
  propertyUsers: z.array(directoryPropertyAssignment),
  shadowable: z.boolean(),
  shadowBlockedReason: z.string().optional(),
});
export type AdminDirectoryPerson = z.infer<typeof adminDirectoryPerson>;

const count = z.number().int().nonnegative();

export const adminPeoplePage = z.object({
  data: z.array(adminDirectoryPerson),
  total: count,
  page: count,
  pageSize: count,
  totalPages: count,
  isPartial: z.boolean(),
  partialReasons: z.array(z.string()).optional(),
});
export type AdminPeoplePage = z.infer<typeof adminPeoplePage>;

const digitsIn = (value: string) => value.replace(/\D/g, "").length;

export const personPhone = z.preprocess(absentOrBlankAsNull, storableText(SETTINGS_SHORT_TEXT_MAX_LENGTH).refine((value) => digitsIn(value) >= PHONE_DIGITS_MIN, `A phone number has at least ${PHONE_DIGITS_MIN} digits.`).nullable());

export const adminPersonPhoneRequest = z.object({ phone: personPhone }).strict();
export type AdminPersonPhoneRequest = { phone?: string | null };

export const adminPersonPhone = z.object({ id: recordId, phone: z.string().nullable() });
export type AdminPersonPhone = z.infer<typeof adminPersonPhone>;

export const adminPassword = z
  .string()
  .min(ADMIN_PASSWORD_MIN_LENGTH, `A password has at least ${ADMIN_PASSWORD_MIN_LENGTH} characters.`)
  .max(ADMIN_PASSWORD_MAX_LENGTH, `A password has at most ${ADMIN_PASSWORD_MAX_LENGTH} characters.`);

export const adminPersonPasswordRequest = z.object({ password: adminPassword }).strict();
export type AdminPersonPasswordRequest = z.infer<typeof adminPersonPasswordRequest>;
